import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { handleApiError } from '../lib/api-client';

interface UploadImageResponse {
  success: boolean;
  message: string;
  data: {
    id: string;
    name: string;
    url: string;
    size: number;
    mimeType: string;
    originalName: string;
    facilityAssessmentId: string | null;
  };
}

// Compress image before upload to handle large mobile photos
const compressImage = (
  file: File,
  maxWidth: number = 1920,
  maxHeight: number = 1920,
  quality: number = 0.8
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = event => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions while maintaining aspect ratio
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        // Draw image on canvas
        ctx.drawImage(img, 0, 0, width, height);

        // Convert canvas to blob
        canvas.toBlob(
          blob => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Could not compress image'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
    };
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
  });
};

const uploadImage = async (file: File): Promise<UploadImageResponse> => {
  try {
    console.log('Original file:', {
      name: file.name,
      size: file.size,
      type: file.type,
    });

    // Compress image before upload
    let fileToUpload: File | Blob = file;

    // Only compress if it's an image and larger than 500KB
    if (file.type.startsWith('image/') && file.size > 500000) {
      console.log('Compressing image...');
      const compressedBlob = await compressImage(file);

      // Create a new File object from the compressed blob
      fileToUpload = new File([compressedBlob], file.name, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });

      console.log('Compressed file:', {
        originalSize: file.size,
        compressedSize: fileToUpload.size,
        reduction: ((1 - fileToUpload.size / file.size) * 100).toFixed(1) + '%',
      });
    }

    const formData = new FormData();
    formData.append('file', fileToUpload);

    // Get auth token from localStorage
    const token = localStorage.getItem('access_token');

    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    console.log('Uploading image:', {
      name: file.name,
      size: fileToUpload.size,
      type: fileToUpload.type,
      hasToken: !!token,
      apiUrl: `${import.meta.env.VITE_API_URL}/api/v1/assets/upload`,
    });

    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/assets/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });

    console.log('Upload response status:', response.status, response.statusText);

    if (!response.ok) {
      let errorMessage = 'Upload failed';
      try {
        const error = await response.json();
        console.error('Upload failed with error:', error);
        errorMessage = error.message || errorMessage;
      } catch (e) {
        console.error('Failed to parse error response:', e);
        errorMessage = `Upload failed with status ${response.status}: ${response.statusText}`; 
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('Upload successful:', result);
    return result;
  } catch (error) {
    console.error('Upload error:', error);
    throw error;
  }
};

const deleteImage = async (id: string): Promise<void> => {
  // Get auth token from localStorage
  const token = localStorage.getItem('access_token');

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  console.log('Deleting image:', { id, hasToken: !!token });

  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/assets/${id}`, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    let errorMessage = 'Delete failed';
    try {
      const error = await response.json();
      console.error('Delete failed:', error);
      errorMessage = error.message || errorMessage;
    } catch (e) {
      console.error('Failed to parse error response:', e);
    }
    throw new Error(errorMessage);
  }

  console.log('Image deleted successfully');
};

export const useUploadImage = () => {
  return useMutation({
    mutationFn: uploadImage,
    onSuccess: data => {
      console.log('Upload mutation successful:', data);
      return data;
    },
    onError: error => {
      console.error('Upload mutation error:', error);
      // Error toast is handled in the captureFromCamera function
      // to provide more specific error messages
    },
  });
};

// Hook for camera capture and image management
export const useCameraCapture = () => {
  const uploadMutation = useUploadImage();

  const captureFromCamera = async (): Promise<UploadImageResponse['data'] | null> => {
    return new Promise(resolve => {
      // Create camera input element
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';

      // Mobile compatibility: capture attribute
      // On mobile browsers, this opens the camera app
      input.setAttribute('capture', 'environment');

      let isProcessing = false;
      let focusHandlerAdded = false;

      const cleanup = () => {
        try {
          console.log('Cleaning up camera input...');
          if (focusHandlerAdded) {
            window.removeEventListener('focus', handleFocus);
            focusHandlerAdded = false;
          }
          if (input.parentNode) {
            document.body.removeChild(input);
          }
        } catch (e) {
          console.error('Error cleaning up input element:', e);
        }
      };

      input.onchange = async e => {
        if (isProcessing) {
          console.log('Already processing a file, ignoring duplicate event');
          return;
        }
        isProcessing = true;

        const file = (e.target as HTMLInputElement).files?.[0];

        console.log('File selected from camera:', {
          hasFile: !!file,
          name: file?.name,
          size: file?.size,
          type: file?.type,
          timestamp: new Date().toISOString(),
        });

        if (!file) {
          console.log('No file selected');
          cleanup();
          resolve(null);
          return;
        }

        // Validate that it's an image (be more lenient for mobile)
        if (!file.type || !file.type.startsWith('image/')) {
          console.warn('File type is not explicitly image, but attempting upload anyway:', file.type);
          // Don't reject immediately - some mobile browsers don't set the type correctly
        }

        // Check file size (warn if too large, but don't reject)
        if (file.size > 10 * 1024 * 1024) {
          // 10MB
          console.warn('Large file detected:', (file.size / 1024 / 1024).toFixed(2), 'MB');
        }

        try {
          console.log('Starting upload for captured image...');
          toast.loading('Uploading image...', { id: 'upload-toast' });

          const result = await uploadMutation.mutateAsync(file);

          console.log('Upload complete:', result);
          toast.success('Image uploaded successfully', { id: 'upload-toast' });
          cleanup();
          resolve(result.data);
        } catch (error) {
          console.error('Capture/upload failed:', error);
          const errorMessage = error instanceof Error ? error.message : 'Failed to upload captured image';
          toast.error(errorMessage, { id: 'upload-toast' });
          cleanup();
          resolve(null);
        }
      };

      // Handle cancel/dismiss
      input.oncancel = () => {
        console.log('Camera input cancelled by user');
        cleanup();
        resolve(null);
      };

      // Handle focus return (when user dismisses camera without selecting)
      const handleFocus = () => {
        // Give some time for onchange to fire if a photo was taken
        setTimeout(() => {
          if (!isProcessing) {
            console.log('Focus returned without file selection, cleaning up');
            cleanup();
          }
        }, 1500); // Increased timeout for slower devices
      };

      // Add to body temporarily for better mobile compatibility
      input.style.display = 'none';
      input.style.position = 'absolute';
      input.style.left = '-9999px';
      document.body.appendChild(input);

      // Listen for when user returns to the app (in case they cancel)
      // This helps clean up on mobile when user backs out of camera
      window.addEventListener('focus', handleFocus, { once: false });
      focusHandlerAdded = true;

      // Small delay before triggering to ensure DOM is ready
      setTimeout(() => {
        console.log('Triggering camera input...');
        input.click();
      }, 100);
    });
  };

  return {
    captureFromCamera,
    deleteImage,
    isUploading: uploadMutation.isPending,
  };
};
